import { ENV } from '../../config/runtimeEnv';
import {
	ProcessingGroup,
	GroupStepInstance,
	SampleProtocolInstance,
	AnalysisProtocolInstance,
	PaginatedProcessingGroupResponse,
	GroupProcessingStatistics,
	CreateProcessingGroupRequest,
	StartGroupProcessingRequest,
	CompleteGroupStepRequest,
	SaveGroupStepDraftRequest,
	FailInstanceRequest,
	StartGroupStepRequest,
	HoldGroupRequest,
	FailGroupRequest,
	GroupProcessingFilter,
	StepActionLog
} from "../../types/lab/ProtocolTypes";
import type { LegacyUserId } from "../../types/identity";
import { sessionLabiqUserId } from "../../utils/auth/labiqUserId";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_LABIQ_SERVER;

// Get all processing groups with filtering and pagination
export const fetchProcessingGroups = async (
	filters: GroupProcessingFilter = {}
): Promise<PaginatedProcessingGroupResponse> => {
	const params = new URLSearchParams();

	Object.entries(filters).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			params.append(key, value.toString());
		}
	});

	const response = await http.get(`${API_BASE_URL}/group-processing?${params.toString()}`);

	return response.data;
};

// Get a specific processing group
export const fetchProcessingGroup = async (id: number): Promise<ProcessingGroup> => {
	const response = await http.get(`${API_BASE_URL}/group-processing/${id}`);

	return response.data;
};

// Create a new processing group
export const createProcessingGroup = async (
	request: CreateProcessingGroupRequest
): Promise<ProcessingGroup> => {
	const response = await http.post(
		`${API_BASE_URL}/group-processing`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data.group;
};

// Delete a processing group
export const deleteProcessingGroup = async (id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/group-processing/${id}`);
};

// Start processing for a group
export const startGroupProcessing = async (
	groupId: number,
	request: StartGroupProcessingRequest
): Promise<ProcessingGroup> => {
	const response = await http.post(
		`${API_BASE_URL}/group-processing/${groupId}/start`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data.group;
};

export const completeGroupProcessing = async (groupId: number, request: { performed_by: LegacyUserId; notes?: string; finish_samples?: boolean; }): Promise<{ group: ProcessingGroup; }> => {
	const response = await http.post(
		`${API_BASE_URL}/group-processing/${groupId}/complete`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return {
		group: response.data.group,
	};
};

// Complete a step for a processing group
export const completeGroupStep = async (
	groupId: number,
	request: CompleteGroupStepRequest
): Promise<ProcessingGroup> => {
	const response = await http.post(
		`${API_BASE_URL}/group-processing/${groupId}/step/complete`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data.group;
};

// Start a step for a processing group
export const startGroupStep = async (
	groupId: number,
	request: StartGroupStepRequest
): Promise<{ group: ProcessingGroup; step_instance: any }> => {
	const response = await http.post(
		`${API_BASE_URL}/group-processing/${groupId}/step/start`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data;
};

// Add samples to an existing group
export const addItemsToGroup = async (
	groupId: number,
	groupType: "sample" | "analysis",
	ids: string[]
): Promise<{ group: ProcessingGroup; added_samples: number }> => {

	let requestData = {};
	if(groupType === "sample") {
		requestData = {
			sample_ids: ids,
			performed_by: sessionLabiqUserId(),
		};
	}else{
		requestData = {
			analysis_ids: ids,
			performed_by: sessionLabiqUserId(),
		};
	}

	const response = await http.post(
		`${API_BASE_URL}/group-processing/${groupId}/items/add`,
		{
			...requestData
		},
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data;
};

// Remove a sample from a group
export const removeItemFromGroup = async (
	groupId: number,
	itemId: string,
	itemType: "sample" | "analysis"
): Promise<ProcessingGroup> => {
	const response = await http.delete(
		`${API_BASE_URL}/group-processing/${groupId}/items/${itemId}`,
		{
			params: {
				item_type: itemType,
			},
		}
	);

	return response.data.group;
};

// Put a group on hold
export const putGroupOnHold = async (
	groupId: number,
	request: HoldGroupRequest = {}
): Promise<ProcessingGroup> => {
	const response = await http.put(
		`${API_BASE_URL}/group-processing/${groupId}/hold`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data.group;
};

// Resume a group from hold
export const resumeGroupFromHold = async (groupId: number): Promise<ProcessingGroup> => {
	const response = await http.put(
		`${API_BASE_URL}/group-processing/${groupId}/resume`,
		{}
	);

	return response.data.group;
};

// Mark a group as failed
export const markGroupAsFailed = async (
	groupId: number,
	request: FailGroupRequest = {}
): Promise<ProcessingGroup> => {
	const response = await http.put(
		`${API_BASE_URL}/group-processing/${groupId}/fail`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data.group;
};

// Get group processing statistics
export const fetchGroupProcessingStatistics = async (filters: {
	date_from?: string;
	date_to?: string;
} = {}): Promise<GroupProcessingStatistics> => {
	const params = new URLSearchParams();

	Object.entries(filters).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			params.append(key, value.toString());
		}
	});

	const response = await http.get(
		`${API_BASE_URL}/group-processing/statistics/overview?${params.toString()}`
	);

	return response.data;
};

// Get action logs for a group
export const fetchGroupActionLogs = async (groupId: number): Promise<StepActionLog[]> => {
	const response = await http.get(
		`${API_BASE_URL}/group-processing/${groupId}/actions/logs`
	);

	return response.data;
};

// Save a draft of a step (does not complete it)
export const saveGroupStepDraft = async (
	groupId: number,
	request: SaveGroupStepDraftRequest
): Promise<GroupStepInstance> => {
	const response = await http.post(
		`${API_BASE_URL}/group-processing/${groupId}/step/draft`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data.group_step_instance;
};

// Mark an individual sample or analysis instance as failed
export const failGroupInstance = async (
	groupId: number,
	instanceId: number,
	request: FailInstanceRequest
): Promise<{ instance: SampleProtocolInstance | AnalysisProtocolInstance; group_status: string }> => {
	const response = await http.post(
		`${API_BASE_URL}/group-processing/${groupId}/instances/${instanceId}/fail`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data;
};